/**
 * Quick Sale · Recent-items helper.
 *
 * Mirrors the Historical Entry recent-tile pattern (`cu.hist.recentIds`
 * + `pushRecent` in `AdminHistoricalEntry.jsx`) with two deliberate
 * differences:
 *
 *   1. Independent localStorage namespace — the Quick Sale Recent list
 *      MUST NEVER include Historical Entry activity, so we key it under
 *      `cu.qs.recent*` instead of `cu.hist.recent*`.
 *   2. Full snapshot rehydration — Historical Entry only stores an id
 *      because its Quick Items catalog is small and always loaded on
 *      the page. Quick Sale's Recent bank includes Shopify products
 *      that would otherwise require an extra fetch to rehydrate, so we
 *      persist a minimal canonical snapshot ({shopify_variant_id,
 *      shopify_product_id, title, unit_price, image_url, taxable, sku,
 *      handle, id}) directly. This preserves canonical Shopify variant
 *      identity even after a mirror re-sync drops the underlying row.
 *
 * The module is intentionally standalone (no React imports) so it can
 * be exercised by a plain node smoke test — the codebase's Jest/Babel
 * ESM setup is currently non-functional (see handoff Issue 1) and this
 * keeps a real regression net in place without touching that config.
 */

export const LS_KEYS = {
  recentIds:  "cu.qs.recentIds",
  recentMap:  "cu.qs.recentMap",
};

/** Max Recent tiles rendered. Matches Historical Entry (6). */
export const RECENT_LIMIT = 6;
/** Max Recent snapshots retained on-device. */
export const RECENT_STORE_LIMIT = 20;


// ── Canonical identity ────────────────────────────────────────────
/**
 * Build the canonical Recent key for one product or quick item.
 * Preserves Shopify variant identity FIRST (owner directive — canonical
 * identity is the sole reason we track Recent at all). Falls back to
 * the Shopify product id, then internal id.
 */
export function recentKeyForProduct(p) {
  if (!p || typeof p !== "object") return null;
  if (p.shopify_variant_id) return `shopify:variant:${p.shopify_variant_id}`;
  if (p.shopify_product_id) return `shopify:product:${p.shopify_product_id}`;
  if (p.id)                 return `shopify:local:${p.id}`;
  return null;
}

export function recentKeyForQuickItem(item) {
  if (!item || typeof item !== "object" || !item.id) return null;
  return `qi:${item.id}`;
}


// ── Storage adapters ──────────────────────────────────────────────
function _readJSON(store, key, fallback) {
  try {
    const raw = store.getItem(key);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch { return fallback; }
}

function _writeJSON(store, key, val) {
  try { store.setItem(key, JSON.stringify(val)); }
  catch { /* quota / unavailable — silent */ }
}


// ── Public API ────────────────────────────────────────────────────
/**
 * Persist a Recent entry and return the new ordered id list.
 * Duplicates move to the front; snapshot fields are refreshed on every
 * push so rename / price changes propagate immediately.
 */
export function pushRecent(store, snapshot) {
  if (!store || !snapshot || !snapshot.key) return _readJSON(store, LS_KEYS.recentIds, []);
  const ids = _readJSON(store, LS_KEYS.recentIds, []);
  const map = _readJSON(store, LS_KEYS.recentMap, {});
  const nextIds = [snapshot.key,
                     ...ids.filter((k) => k !== snapshot.key)].slice(0, RECENT_STORE_LIMIT);
  const nextMap = { ...map, [snapshot.key]: snapshot };
  // Drop map entries for ids that fell off the tail so we don't grow
  // unbounded on hot devices.
  const kept = new Set(nextIds);
  for (const k of Object.keys(nextMap)) {
    if (!kept.has(k)) delete nextMap[k];
  }
  _writeJSON(store, LS_KEYS.recentIds, nextIds);
  _writeJSON(store, LS_KEYS.recentMap, nextMap);
  return nextIds;
}

export function readRecentSnapshots(store, limit = RECENT_LIMIT) {
  if (!store) return [];
  const ids = _readJSON(store, LS_KEYS.recentIds, []);
  const map = _readJSON(store, LS_KEYS.recentMap, {});
  const out = [];
  for (const k of ids) {
    const snap = map[k];
    if (snap && (snap.kind === "shopify" || snap.kind === "quick_item")) {
      out.push(snap);
      if (out.length >= limit) break;
    }
  }
  return out;
}


// ── Snapshot builders ─────────────────────────────────────────────
export function shopifyProductSnapshot(p) {
  const key = recentKeyForProduct(p);
  if (!key) return null;
  return {
    key,
    kind: "shopify",
    id: p.id || null,
    shopify_product_id: p.shopify_product_id || null,
    shopify_variant_id: p.shopify_variant_id || null,
    title: p.title || "",
    unit_price: (typeof p.unit_price === "number") ? p.unit_price : Number(p.unit_price) || 0,
    image_url: p.image_url || null,
    taxable:   p.taxable !== false,
    sku:       p.sku    || null,
    handle:    p.handle || null,
    // Timestamp is a debugging aid only — ordering is done by list
    // position, never by comparing timestamps (avoids clock-skew bugs).
    _touched_at: new Date().toISOString(),
  };
}

export function quickItemSnapshot(item) {
  const key = recentKeyForQuickItem(item);
  if (!key) return null;
  return {
    key,
    kind: "quick_item",
    id: item.id,
    name: item.name || "",
    category: item.category || null,
    default_total: (item.default_total == null) ? null : Number(item.default_total),
    _touched_at: new Date().toISOString(),
  };
}
